import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * The three Cognito-fronted environments customers see in the env
 * switcher. The union is fixed at the API level — every BFF proxy
 * lambda routes by the env name.
 *
 * Per CPF-NFR-025 the portal is **four** Amplify apps (1 landing + 1
 * per env), and per CPF-NFR-034 each per-env portal bundle is
 * **env-locked**: the `Environment` here is the env THIS bundle was
 * built for, NOT a runtime-mutable selection.
 */
export type Environment = "sandbox" | "development" | "production";

export const ENVIRONMENT_OPTIONS: Environment[] = [
  "sandbox",
  "development",
  "production",
];

/**
 * THIS portal's own environment, baked in at build time by the per-env
 * Amplify deploy via `VITE_TARGET_ENV` (portal-amplify-stack.ts injects
 * `prod | sandbox | dev`). In the v3 multi-Amplify topology (CPF-NFR-025)
 * each env is a SEPARATE Amplify app on its own host — so a portal must
 * read its env from the bundle, never from a runtime selector.
 *
 * Bug history (CPF-NFR-034 / `foundations/AI-PITFALLS.md` P-033): this
 * file used to expose a `setEnv` API that flipped `env` in React state +
 * persisted to `localStorage`. The Topbar dropdown used it to "switch
 * env" inside the SAME bundle, which:
 *   - kept the URL on the production host while flipping the proxy path
 *     to `/api/proxy/clients/sandbox/...`;
 *   - sent the prod-issued Cognito Bearer to the sandbox upstream
 *     (`api.clients.rook-connect.review`), which 5xx'd because the
 *     token's issuer / tenant didn't match.
 * Env switching is cross-origin navigation via `<EnvSwitchButton>`
 * (CPF-NFR-028 + `packages/shared/src/components/EnvSwitchButton.tsx`),
 * never an in-bundle path-flip. The `setEnv` + localStorage path is gone.
 *
 * `ownEnv()` defends in depth: missing/unknown `VITE_TARGET_ENV` falls
 * back to "production" (NEVER silently claim a lower env than reality —
 * showing "sandbox" while serving prod was the exact 2026-06-02 bug).
 */
const TARGET_ENV_MAP: Record<string, Environment> = {
  prod: "production",
  production: "production",
  sandbox: "sandbox",
  dev: "development",
  development: "development",
};

const ownEnv = (): Environment => {
  // MUST use the LITERAL `import.meta.env.VITE_TARGET_ENV` expression so Vite
  // statically inlines the build-time value. There is NO runtime
  // `import.meta.env` object in a Vite bundle — Vite replaces the literal
  // member expression at build time only. The previous version aliased
  // `const meta = import.meta; meta.env?.VITE_TARGET_ENV`, which Vite does
  // NOT replace (it only matches the literal `import.meta.env.<KEY>`), so at
  // runtime `meta.env` was `undefined` and EVERY per-env bundle fell back to
  // "production" — the sandbox/dev portals showed ENV=Production AND routed
  // their API calls to the PROD upstream (caught 2026-06-08: prod data + the
  // "Production" label on the sandbox host). The `as Record<...>` cast erases
  // at compile time, leaving `import.meta.env.VITE_TARGET_ENV` for Vite to
  // inline, and keeps it typechecking in @rook/shared (consumed by the
  // landing too, where the var is simply absent → undefined → production,
  // which is fine: the landing doesn't use the per-env switcher).
  const target =
    (import.meta.env as Record<string, string | undefined>).VITE_TARGET_ENV ??
    "";
  return TARGET_ENV_MAP[target] ?? "production";
};

type AppContextValue = {
  /**
   * The env this bundle was built for. Derived ONCE from
   * `VITE_TARGET_ENV` at provider mount; NEVER mutates at runtime.
   * Every feature view in `packages/portal/src/features/**` reads
   * this and threads it into proxy paths
   * (`/api/proxy/clients/${env}/...`).
   *
   * To navigate to a different env, use `<EnvSwitchButton>` from
   * `@rook/shared/components/EnvSwitchButton` with a `targetOrigin`
   * resolved through `portalOriginForEnv(env)` — that is a
   * cross-origin browser navigation, not a context mutation.
   */
  readonly env: Environment;
};

const AppContext = createContext<AppContextValue | null>(null);

/**
 * App-wide read-only state container. Today: just the active
 * environment (locked to the build-time `VITE_TARGET_ENV` per
 * CPF-NFR-034). Future read-only state can land here; runtime
 * mutability of `env` is forbidden by the requirement.
 *
 * Auth state is NOT here — that lives in `<AuthProvider>` so the
 * session query is React Query-cached + revalidated on focus.
 */
export const AppProvider = ({ children }: { children: ReactNode }) => {
  // `env` is derived once at mount; the value is referentially stable
  // for the lifetime of the provider, so a useMemo over the lone field
  // is enough to give consumers a stable context value (and avoid
  // unnecessary re-renders).
  const value = useMemo<AppContextValue>(() => ({ env: ownEnv() }), []);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextValue => {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useApp must be used within <AppProvider>");
  }
  return ctx;
};
